import * as THREE from 'three';
import type { BattleState, MapDef } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import type { OmenCtx } from '../omens/common.ts';
import { SCALE, WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';
import { captureSky, paintHour, seaMat, type Sky } from './shore.ts';
import type { TerrainBuild } from './terrain.ts';

/**
 * The Maw's night (La Orilla 3, docs/SAGAS.md): the tip of the titan's fin. A stone lighthouse stands where the node
 * would, its lantern room lit and a pale warm beam turning out over the sea; three iron braziers round it (`rules.maw.
 * braziers`), lit or put out (`s.maw.lit`, with a ring that fills as one is doused or lit again). The sea lies past the
 * clearing's rim on the side away from the roads' mouths (as sim/campaign `mawOf`), surf on black rocks.
 *
 * The Maw itself and its light's bulb are enemy models (`maw`, `lure`); this layer draws the stalk between them: an arch
 * from the Maw's crown out to the bulb while it shines, and drooping into the water while it is broken. While lit, faint
 * cold motes drift from the heroes it sees towards its mouth (the drag), and the bulb throws a sickly pool on the ground.
 * Dark, the sea goes black. Felled, the Maw sinks in a burst of ink and the lighthouse's beam swings onto the water where
 * it lay, then nothing.
 *
 * Draw calls: the coast (one merged mesh), the lamps (one), the sea (one), the stalk (two, one shown), the beam (one), a
 * glow layer. No lights.
 */

type Rules = NonNullable<NonNullable<BattleState['rules']>['maw']>;

const K = WORLD_PER_HEX / 1000;
const LAMP = new THREE.Color('#ffd6a0');
const BEAM = new THREE.Color('#ffe6c8');
const FIRE = new THREE.Color('#ff8a3a');
const EMBER = new THREE.Color('#ff4f3a');
const COLD = new THREE.Color('#c8ffd0');
const SMOKE = new THREE.Color('#8a8f98');
const ROCK = ['#3a3c40', '#46484c', '#2e3034'];
const WET = new THREE.Color('#4a4c4e');
const STONE = ['#8a8680', '#7a766e', '#6a665e'];
/** How high the Maw's crown and the bulb's top stand, in model units (maw.ts, lure model). */
const CROWN = { y: 0.98, z: -0.3 };
const BULB_TOP = 2.05;

export class LighthouseLayer {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private glow = new GlowLayer(220);
  private seaM: THREE.ShaderMaterial;
  private beam: THREE.Mesh;
  private beamM: THREE.MeshBasicMaterial;
  private arch: THREE.Mesh;
  private droop: THREE.Mesh;
  private lantern: THREE.Vector3;
  private braziers: THREE.Vector3[] = [];
  private rim: THREE.Vector3[] = [];
  private mawP: THREE.Vector3;
  private mouth: THREE.Vector3;
  private bulb: THREE.Vector3;
  private seaDir: { x: number; y: number };
  private sky: Sky;
  private darkNow = -1;
  private now = 0;
  /** When it fell (frame clock): the beam swings onto it and holds. */
  private felled = -1;
  private v = new THREE.Vector3();

  constructor(
    private ctx: OmenCtx,
    private map: MapDef,
    private r: Rules,
  ) {
    this.group.name = 'lighthouse';
    // The way in from the sea, as the sim lays it: from the roads' mouths' side; the sea is the other side.
    let gx = 0;
    let gy = 0;
    for (const sec of map.sectors) {
      gx += sec.gate.q * 1000 + (sec.gate.r * 1000) / 2;
      gy += sec.gate.r * 866;
    }
    const gl = Math.hypot(gx, gy) || 1;
    const dx = gl > 1 ? gx / gl : 1;
    const dy = gl > 1 ? gy / gl : 0;
    this.seaDir = { x: Math.round(-dx * 1000), y: Math.round(-dy * 1000) };
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 10);
    const cone = new THREE.ConeGeometry(0.5, 1, 10);
    const rock = new THREE.DodecahedronGeometry(0.5, 0);
    const ball = new THREE.SphereGeometry(0.5, 8, 6);
    const kit = new Kit();
    const lit = new Kit();
    const rng = mulberry(Math.round(dx * 997 + dy * 613) + 31);
    const R = (): number => rng() * 2 - 1;

    // ---- The lighthouse on the node: a tapering stone tower in courses, a gallery, a lantern room and a cap.
    {
      const g0 = ctx.ground(0, 0);
      const H = 3.3;
      const courses = 8;
      for (let i = 0; i < courses; i++) {
        const t = i / courses;
        const rad = 0.78 - 0.26 * t;
        kit.add(cyl, STONE[i % 3] as string, place(0, g0 + (H / courses) * (i + 0.5), 0, i * 0.4, new THREE.Vector3(rad * 2, H / courses + 0.01, rad * 2)));
      }
      // A plinth of rough stones and a door facing the land.
      kit.add(cyl, '#5a5650', place(0, g0 + 0.12, 0, 0, new THREE.Vector3(2.0, 0.24, 2.0)));
      for (let i = 0; i < 9; i++) {
        const a = (i / 9) * Math.PI * 2 + 0.2 * R();
        kit.add(rock, ROCK[i % 3] as string, place(Math.cos(a) * 1.05, g0 + 0.12, Math.sin(a) * 1.05, R() * 3, new THREE.Vector3(0.4, 0.28, 0.36)));
      }
      const yawLand = Math.atan2(dx, dy);
      const door = place(0, g0, 0, yawLand);
      kit.add(box, '#2a2018', door, place(0, 0.42, 0.77, 0, new THREE.Vector3(0.34, 0.62, 0.06)));
      kit.add(box, '#4a3e30', door, place(0, 0.76, 0.78, 0, new THREE.Vector3(0.42, 0.08, 0.08)));
      // A slit window or two up the tower, lit.
      for (const h of [1.3, 2.2]) lit.add(box, '#ffc890', door, place(0, h, 0.63 - (h - 1.3) * 0.12, 0, new THREE.Vector3(0.08, 0.22, 0.04)));
      // The gallery: a stone ring with an iron rail.
      const top = g0 + H;
      kit.add(cyl, '#6a665e', place(0, top + 0.06, 0, 0, new THREE.Vector3(1.35, 0.12, 1.35)));
      for (let i = 0; i < 12; i++) {
        const a = (i / 12) * Math.PI * 2;
        kit.add(box, '#2a2a2e', place(Math.cos(a) * 0.62, top + 0.3, Math.sin(a) * 0.62, 0, new THREE.Vector3(0.03, 0.42, 0.03)));
      }
      kit.add(cyl, '#2a2a2e', place(0, top + 0.5, 0, 0, new THREE.Vector3(1.28, 0.03, 1.28)));
      // The lantern room: glass lit warm, iron mullions, a dark cap and a little vane.
      lit.add(cyl, '#ffe2b8', place(0, top + 0.55, 0, 0, new THREE.Vector3(0.82, 0.78, 0.82)));
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2;
        kit.add(box, '#1e1e22', place(Math.cos(a) * 0.41, top + 0.55, Math.sin(a) * 0.41, -a, new THREE.Vector3(0.04, 0.8, 0.04)));
      }
      kit.add(cone, '#2a2a30', place(0, top + 1.17, 0, 0, new THREE.Vector3(1.0, 0.46, 1.0)));
      kit.add(ball, '#2a2a30', place(0, top + 1.42, 0, 0, 0.1));
      this.lantern = new THREE.Vector3(0, top + 0.55, 0);
    }

    // ---- The braziers: iron bowls on tripods, a heap of coal in each.
    for (const b of r.braziers) {
      const w = ctx.at(b.x, b.y, 0);
      const gy = ctx.ground(w.x, w.z);
      for (let k = 0; k < 3; k++) {
        const a = (k / 3) * Math.PI * 2;
        kit.add(box, '#2a2826', place(w.x + Math.cos(a) * 0.16, gy + 0.3, w.z + Math.sin(a) * 0.16, -a, new THREE.Vector3(0.04, 0.62, 0.04), Math.cos(a) * 0.25, Math.sin(a) * 0.25));
      }
      kit.add(new THREE.CylinderGeometry(0.34, 0.2, 0.2, 10), '#3a3632', place(w.x, gy + 0.66, w.z));
      kit.add(ball, '#1a1614', place(w.x, gy + 0.74, w.z, 0, new THREE.Vector3(0.48, 0.16, 0.48)));
      this.braziers.push(new THREE.Vector3(w.x, gy + 0.86, w.z));
    }

    // ---- The sea's edge: black rocks along the rim on the sea side, surf breaking on them.
    const sx = this.seaDir.x / 1000;
    const sy = this.seaDir.y / 1000;
    const seaAt = (map.bastion + 0.55) * 1000;
    const span = (map.radius + 1) * 1000;
    for (let off = -span; off <= span; off += 650) {
      const a = seaAt - 150 + 250 * R();
      const x = sx * a - sy * (off + 150 * R());
      const y = sy * a + sx * (off + 150 * R());
      const w = ctx.at(x, y, 0);
      if (Math.hypot(w.x, w.z) > (map.radius + 2) * WORLD_PER_HEX) continue;
      const gy = ctx.ground(w.x, w.z);
      kit.add(rock, ROCK[Math.floor(rng() * 3)] as string, place(w.x, gy + 0.15, w.z, R() * 3, new THREE.Vector3(0.6 + 0.3 * rng(), 0.45 + 0.3 * rng(), 0.55 + 0.3 * rng())));
      this.rim.push(new THREE.Vector3(w.x, gy + 0.25, w.z));
    }

    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.86, metalness: 0.03, flatShading: true });
    const litMat = new THREE.MeshBasicMaterial({ vertexColors: true, toneMapped: false });
    this.mats.push(mat, litMat);
    for (const [k, mm, name] of [
      [kit, mat, 'lighthouse-land'],
      [lit, litMat, 'lighthouse-lamps'],
    ] as const) {
      const mesh = k.build(mm, name);
      if (!mesh) continue;
      mesh.receiveShadow = mm === mat;
      mesh.castShadow = mm === mat && !ctx.low;
      this.geos.push(mesh.geometry);
      this.group.add(mesh);
    }

    // ---- The sea, past the rim on the sea side.
    let top = -Infinity;
    for (const p of this.rim) top = Math.max(top, p.y - 0.25);
    if (!Number.isFinite(top)) top = ctx.ground(0, 0);
    const big = (map.radius + 22) * WORLD_PER_HEX;
    const seaG = new THREE.PlaneGeometry(big * 2, big * 2, 64, 64).rotateX(-Math.PI / 2);
    this.geos.push(seaG);
    this.seaM = seaMat(this.seaDir);
    this.seaM.uniforms.front!.value = seaAt;
    this.seaM.uniforms.foam!.value = 380;
    this.seaM.uniforms.night!.value = 0.8;
    this.mats.push(this.seaM);
    const sea = new THREE.Mesh(seaG, this.seaM);
    sea.position.y = top + 0.12;
    sea.name = 'lighthouse-sea';
    sea.renderOrder = 3;
    sea.frustumCulled = false;
    this.group.add(sea);

    // ---- The beam: a long, faint cone of warm light from the lantern, turning out over the sea.
    const beamG = new THREE.ConeGeometry(0.9, 11, 18, 8, true).translate(0, -5.5, 0);
    // Bright at the lantern, fading to nothing at its far end (additive: black is clear).
    {
      const pos = beamG.getAttribute('position') as THREE.BufferAttribute;
      const col: number[] = [];
      for (let i = 0; i < pos.count; i++) {
        const t = Math.min(1, Math.max(0, -pos.getY(i) / 11));
        const k = (1 - t) * (1 - t);
        col.push(BEAM.r * k, BEAM.g * k, BEAM.b * k);
      }
      beamG.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
      beamG.rotateX(-Math.PI / 2);
    }
    this.geos.push(beamG);
    this.beamM = new THREE.MeshBasicMaterial({ vertexColors: true, transparent: true, opacity: 0.1, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide, toneMapped: false });
    this.mats.push(this.beamM);
    this.beam = new THREE.Mesh(beamG, this.beamM);
    this.beam.position.copy(this.lantern);
    this.beam.name = 'lighthouse-beam';
    this.beam.frustumCulled = false;
    this.beam.renderOrder = 4;
    this.group.add(this.beam);

    // ---- The stalk: from the Maw's crown arching out over the way to its mouth to the bulb; and drooped, broken.
    const ms = SCALE.boss * 1.7;
    const ls = SCALE.enemy * 1.15;
    const mw = ctx.at(r.at.x, r.at.y, 0);
    const gm = ctx.ground(mw.x, mw.z);
    this.mawP = new THREE.Vector3(mw.x, gm, mw.z);
    const lw = ctx.at(r.stalkAt.x, r.stalkAt.y, 0);
    const gl2 = ctx.ground(lw.x, lw.z);
    const toLand = new THREE.Vector3(lw.x - mw.x, 0, lw.z - mw.z).normalize();
    const crown = new THREE.Vector3(mw.x, gm + CROWN.y * ms, mw.z).addScaledVector(toLand, CROWN.z * ms);
    this.mouth = new THREE.Vector3(mw.x, gm + 0.1 * ms, mw.z).addScaledVector(toLand, 0.3 * ms);
    const tip = new THREE.Vector3(lw.x, gl2 + BULB_TOP * ls, lw.z);
    this.bulb = new THREE.Vector3(lw.x, gl2 + 1.55 * ls, lw.z);
    const mid = crown.clone().lerp(tip, 0.45);
    mid.y = Math.max(crown.y, tip.y) + 0.55;
    const archG = new THREE.TubeGeometry(new THREE.CatmullRomCurve3([crown, crown.clone().lerp(mid, 0.5).setY(mid.y - 0.2), mid, tip], false, 'catmullrom', 0.4), 24, 0.07, 6);
    const dmid = crown.clone().lerp(tip, 0.35);
    dmid.y = crown.y + 0.3;
    const dend = crown.clone().lerp(tip, 0.6);
    dend.y = gm + 0.05;
    const droopG = new THREE.TubeGeometry(new THREE.CatmullRomCurve3([crown, dmid, dend], false, 'catmullrom', 0.5), 18, 0.06, 6);
    const stalkM = new THREE.MeshStandardMaterial({ color: '#1a1424', roughness: 0.25, metalness: 0.3 });
    this.geos.push(archG, droopG);
    this.mats.push(stalkM);
    this.arch = new THREE.Mesh(archG, stalkM);
    this.droop = new THREE.Mesh(droopG, stalkM);
    this.arch.name = 'maw-stalk';
    this.droop.name = 'maw-stalk-broken';
    this.droop.visible = false;
    this.arch.castShadow = !ctx.low;
    this.group.add(this.arch, this.droop);

    this.group.add(this.glow.mesh);
    for (const gg of [box, cyl, cone, rock, ball]) gg.dispose();
    this.group.updateMatrixWorld(true);
    // Night at the fin's tip: a moonlit dark from the start.
    this.sky = captureSky(ctx);
    paintHour(ctx, this.sky, 1, 0);
  }

  /** Darkens the terrain past the rim into wet rock, and clears what grows out there (under the sea). */
  apply(terrain: TerrainBuild): void {
    const sx = this.seaDir.x / 1000;
    const sy = this.seaDir.y / 1000;
    const edge = (this.map.bastion + 0.55) * 1000;
    const outOf = (wx: number, wz: number): number => (wx / K) * sx + (wz / K) * sy;
    const c = new THREE.Color();
    const tint = (mesh: THREE.Mesh): void => {
      const geo = mesh.geometry;
      const col = geo.getAttribute('color') as THREE.BufferAttribute | undefined;
      const pos = geo.getAttribute('position') as THREE.BufferAttribute | undefined;
      if (!col || !pos) return;
      mesh.updateMatrixWorld(true);
      const p = new THREE.Vector3();
      for (let i = 0; i < pos.count; i++) {
        p.fromBufferAttribute(pos, i).applyMatrix4(mesh.matrixWorld);
        const t = (outOf(p.x, p.z) - (edge - 900)) / 900;
        if (t <= 0) continue;
        c.setRGB(col.getX(i), col.getY(i), col.getZ(i)).lerp(WET, Math.min(1, t) * 0.8);
        col.setXYZ(i, c.r, c.g, c.b);
      }
      col.needsUpdate = true;
    };
    tint(terrain.mesh);
    const zero = new THREE.Matrix4().makeScale(0, 0, 0);
    const m = new THREE.Matrix4();
    const p = new THREE.Vector3();
    terrain.decor.traverse((o) => {
      if (o.name === 'outskirts-ground' || o.name === 'roads') tint(o as THREE.Mesh);
      const im = o as THREE.InstancedMesh;
      if (!im.isInstancedMesh || o.name === 'rocks') return;
      im.updateMatrixWorld(true);
      let hid = false;
      for (let i = 0; i < im.count; i++) {
        im.getMatrixAt(i, m);
        p.setFromMatrixPosition(m).applyMatrix4(im.matrixWorld);
        if (outOf(p.x, p.z) < edge - 300) continue;
        im.setMatrixAt(i, zero);
        hid = true;
      }
      if (hid) im.instanceMatrix.needsUpdate = true;
    });
  }

  /** Every fresh tick: the Maw's moments, the braziers' embers and smoke, the surf on the rocks. */
  tick(s: BattleState): void {
    const low = this.ctx.low;
    for (const e of s._events ?? []) {
      if (e.t !== 'maw') continue;
      const at = this.ctx.at(e.x, e.y, 0);
      at.y = this.ctx.ground(at.x, at.z);
      if (e.kind === 'rise') {
        this.ctx.vfx.burst(this.mouth, { n: low ? 8 : 20, color: '#cfe8ff', speed: 0.08, up: 0.12, size: 0.14, life: 24, gravity: 0.012, spread: 1.6 });
        this.ctx.shake(0.25);
      } else if (e.kind === 'dark') {
        // The bulb bursts: a cold flash, then the sea goes black.
        this.ctx.flare(this.bulb, '#c8ffd0', 4, 16);
        this.ctx.vfx.burst(this.bulb, { n: low ? 8 : 20, color: '#9fe8b8', speed: 0.06, up: 0.04, size: 0.08, life: 22, gravity: 0.01, spread: 0.6 });
        this.ctx.vfx.burst(this.bulb, { n: low ? 4 : 10, color: '#1a1424', speed: 0.05, up: 0.02, size: 0.12, life: 26, gravity: 0.016, spread: 0.5 });
      } else if (e.kind === 'light') {
        this.ctx.flare(this.bulb, '#cfffd8', 3.2, 22);
      } else if (e.kind === 'bite') {
        this.ctx.flare(at.clone().setY(at.y + 0.6), '#ff4f3a', 2, 10);
        this.ctx.vfx.burst(at.clone().setY(at.y + 0.3), { n: low ? 5 : 12, color: '#cfe8ff', speed: 0.07, up: 0.1, size: 0.08, life: 16, gravity: 0.014, spread: 0.5 });
        this.ctx.shake(0.18);
      } else if (e.kind === 'out') {
        this.ctx.vfx.burst(at.clone().setY(at.y + 0.9), { n: low ? 6 : 14, color: '#6a6e76', speed: 0.025, up: 0.06, size: 0.16, life: 34, gravity: -0.004, spread: 0.3 });
      } else if (e.kind === 'lit') {
        this.ctx.flare(at.clone().setY(at.y + 0.95), '#ff9a4a', 2.4, 14);
        this.ctx.vfx.burst(at.clone().setY(at.y + 0.9), { n: low ? 6 : 14, color: '#ff8a3a', speed: 0.04, up: 0.08, size: 0.06, life: 18, gravity: -0.002, spread: 0.3 });
      } else if (e.kind === 'felled') {
        this.felled = this.now;
        // It goes down: a column of ink and white water where it lay.
        this.ctx.shake(0.6);
        this.ctx.flare(this.mouth, '#ffe6c8', 5, 30);
        for (let k = 0; k < (low ? 3 : 6); k++) {
          this.v.copy(this.mawP).add(new THREE.Vector3(Math.sin(k * 2.1) * 0.8, 0.3, Math.cos(k * 2.1) * 0.8));
          this.ctx.vfx.burst(this.v, { n: low ? 6 : 14, color: k % 2 ? '#1a1424' : '#e8f4ff', speed: 0.09, up: 0.16, size: 0.16, life: 30, gravity: 0.014, spread: 0.9 });
        }
        this.ctx.vfx.ring(this.mawP.clone().setY(this.mawP.y + 0.1), 0.4, 3.4, '#cfe8ff', 18, 0.7);
      }
    }
    // Embers off the lit braziers, smoke off the dead ones.
    const st = s.maw;
    if (st && s.tick % (low ? 6 : 3) === 0) {
      const i = s.tick % this.braziers.length;
      const b = this.braziers[i];
      if (b) {
        if (st.lit[i]) this.ctx.vfx.burst(b, { n: low ? 1 : 3, color: '#ff8a3a', speed: 0.015, up: 0.05, size: 0.04, life: 20, gravity: -0.003, spread: 0.15 });
        else this.ctx.vfx.burst(b, { n: 1, color: '#7a7e86', speed: 0.01, up: 0.03, size: 0.12, life: 30, gravity: -0.002, spread: 0.15 });
      }
    }
    // The surf breaking on the rim's rocks.
    if (this.rim.length && s.tick % (low ? 6 : 3) === 0) {
      const p = this.rim[(s.tick * 7) % this.rim.length] as THREE.Vector3;
      this.ctx.vfx.burst(p, { n: low ? 2 : 4, color: '#ffffff', speed: 0.04, up: 0.08, size: 0.09, life: 14, gravity: 0.012, spread: 0.5 });
    }
  }

  /** Every redraw: the stalk, the beam, the sea's darkness, the braziers' fire and rings, the drag's motes. */
  draw(s: BattleState): void {
    const r = this.r;
    const st = s.maw;
    const alive = !!st?.id && !st.done;
    const dark = alive && !!st?.dark;
    const lit = alive && !dark;
    this.arch.visible = lit;
    this.droop.visible = dark;
    this.seaM.uniforms.night!.value = dark ? 1 : 0.8;
    // Its light broken, the night goes a shade blacker; back once it shines again.
    const want = dark ? 1 : 0;
    if (want !== this.darkNow) {
      this.darkNow = want;
      paintHour(this.ctx, this.sky, 1, 0);
      if (dark) {
        if (this.ctx.hemi) this.ctx.hemi.intensity *= 0.7;
        if (this.ctx.key) this.ctx.key.intensity *= 0.7;
      }
    }
    this.seaM.uniforms.time!.value = this.now;

    // The beam: turning out over the sea; at the fall, swung onto where the Maw lay, bright, and then gone.
    const since = this.felled >= 0 ? this.now - this.felled : -1;
    if (since >= 0) {
      const toward = Math.atan2(this.mawP.x - this.lantern.x, this.mawP.z - this.lantern.z);
      this.beam.rotation.y = toward;
      this.beam.rotation.x = 0.28;
      this.beamM.opacity = since < 2.5 ? 0.3 : Math.max(0, 0.3 - (since - 2.5) * 0.2);
    } else {
      this.beam.rotation.y = this.now * 0.55;
      this.beam.rotation.x = 0.1;
      this.beamM.opacity = 0.1;
    }

    this.glow.begin();
    this.glow.add(this.lantern.x, this.lantern.y, this.lantern.z, LAMP, 2.4, 0.7);
    this.glow.add(this.lantern.x, this.lantern.y, this.lantern.z, BEAM, 5, 0.18);
    // The braziers: a fire and a warm pool when lit, a grey wisp when out; a ring filling as one is doused or lit.
    this.braziers.forEach((b, i) => {
      const on = st ? !!st.lit[i] : true;
      const fl = 0.85 + 0.15 * Math.sin(this.now * 11 + i * 2.3) * Math.sin(this.now * 7.3 + i);
      if (on) {
        this.glow.add(b.x, b.y + 0.15, b.z, FIRE, 1.1 * fl, 0.85);
        this.glow.add(b.x, b.y - 0.75, b.z, FIRE, 3.2, 0.2);
      } else this.glow.add(b.x, b.y + 0.1, b.z, SMOKE, 0.7, 0.18);
      const prog = st ? (st.douse[i] ?? 0) / Math.max(1, on ? r.douse : r.relight) : 0;
      if (prog > 0) {
        // A ring of marks round the bowl, filling: red while it is being put out, warm while it catches again.
        const n = 12;
        const filled = Math.ceil(prog * n);
        for (let k = 0; k < n; k++) {
          const a = (k / n) * Math.PI * 2;
          this.glow.add(b.x + Math.cos(a) * 0.55, b.y - 0.7, b.z + Math.sin(a) * 0.55, on ? EMBER : FIRE, 0.22, k < filled ? 0.9 : 0.15);
        }
      }
    });
    // Lit: the bulb's sickly pool on the ground, and cold motes drifting from the heroes it sees to its mouth.
    if (lit) {
      this.glow.add(this.bulb.x, this.bulb.y, this.bulb.z, COLD, 2.2, 0.35);
      this.glow.add(this.bulb.x, this.ctx.ground(this.bulb.x, this.bulb.z) + 0.05, this.bulb.z, COLD, 3.6, 0.16);
      const sight = r.sight * K;
      for (const h of s.heroes) {
        if (h.downTicks > 0) continue;
        const hp = this.ctx.pos(h.id);
        if (!hp || Math.hypot(hp.x - this.mawP.x, hp.z - this.mawP.z) > sight) continue;
        for (let k = 0; k < 4; k++) {
          const t = (this.now * 0.45 + k / 4 + h.id * 0.13) % 1;
          this.v.copy(hp).setY(hp.y + 0.7).lerp(this.mouth, t);
          this.glow.add(this.v.x, this.v.y + 0.25 * Math.sin(t * Math.PI), this.v.z, COLD, 0.32, 0.55 * Math.sin(t * Math.PI));
        }
      }
    }
    this.glow.end();
  }

  frame(now: number): void {
    this.now = now;
  }

  dispose(): void {
    for (const g of this.geos) g.dispose();
    for (const m of this.mats) m.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
  }
}

/** A small seeded random (the layout is the same every time for the same coast). */
const mulberry = (seed: number): (() => number) => {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};
